import type { UserProfile } from '@/types/user';
import type { UserCampsiteMediaResponse } from '@/types/api/user';

import useFetchService from '@/composables/useFetchService';

export default function useUserService() {
  /********************
   * COMPOSITIONS      *
   ********************/
  const runtimeConfig = useRuntimeConfig();
  const fetchService = useFetchService({ base: runtimeConfig.public.apiUrl });
  const route = useRoute();

  /********************
   * FUNCTIONS         *
   ********************/
  function canCreateCampsiteMedia(lang: string) {
    return fetchService.get('/account/check-permission/create_campsite_media/', lang);
  }

  function changeMail(lang: string, data) {
    return fetchService.post('/account/email/change/', lang, data);
  }

  function changePassword(lang: string, data) {
    return fetchService.post('/account/password/change/', lang, data);
  }

  function setInitialPassword(lang: string, data) {
    return fetchService.post('/account/password/set/', lang, data);
  }

  function checkUser(lang: string, params) {
    return fetchService.get('/account/check-user/', lang, { params });
  }

  function getMedia(lang: string, params) {
    return fetchService.get('/account/profile/media/', lang, { params });
  }

  function getMediaForCampsite(lang: string, slug: string): Promise<UserCampsiteMediaResponse> {
    return fetchService.get(`/account/profile/media/${slug}/`, lang);
  }

  function getProfile(lang: string, params?: any) {
    return fetchService.get('/account/profile/', lang, { params });
  }
  function getProfileLanguages(lang) {
    return fetchService.get('/account/profile/languages/', lang);
  }
  function getReviews(lang: string, params) {
    return fetchService.get('/account/profile/reviews/', lang, { params });
  }
  function login(lang: string, data) {
    return fetchService.post<UserProfile>('/account/login/', lang, data);
  }
  function logout() {
    return fetchService.post('/account/logout/', null);
  }
  function register(lang: string, data) {
    return fetchService.post('/account/registration/', lang, data);
  }
  function resendVerificationEmail(lang: string, data) {
    return fetchService.post('/account/registration/resend-verification-email/', lang, data);
  }
  function resetPassword(lang: string, data) {
    return fetchService.post('/account/password/reset/', lang, data);
  }
  function setNewPassword(lang: string, data) {
    return fetchService.post('/account/password/reset/confirm/', lang, data);
  }
  function setProfile(lang: string, data) {
    return fetchService.post('/account/profile/update/', lang, data);
  }
  function setProfilePicture(lang: string, data) {
    return fetchService.post('/account/profile/picture/', lang, data);
  }
  function toggleFavorite(data) {
    return fetchService.post('/account/profile/toggle-favorite/', null, data);
  }
  function update(lang: string, data) {
    return fetchService.post('/account/profile/', lang, data);
  }
  function verifyChangedEmail(lang: string, params) {
    return fetchService.get('/account/email/change/confirm/', lang, { params });
  }
  function verifyEmail(lang: string, data) {
    return fetchService.post('/account/registration/verify-email/', lang, data);
  }
  function updateInsider(lang: string, insiderUid: string | null) {
    if (insiderUid) {
      fetchService.get('/account/insider/', lang, { params: { uid: insiderUid } });
    }
  }
  function confirmDoubleOptin(lang: string, token: string, insiderUid: string) {
    return fetchService.get('/insider/email-optin/', lang, { params: { token: token, id: insiderUid } });
  }
  function getMarketingPreferences(lang: string, token: string, insiderUid: string) {
    return fetchService.get('/insider/marketing-preferences/', lang, { params: { token: token, id: insiderUid } });
  }
  function saveMarketingPreferences(lang: string, token: string, insiderUid: string, topics: string[]) {
    return fetchService.post('/insider/marketing-preferences/', lang, { token: token, id: insiderUid, save_topics: topics });
  }
  function subscribeToSingleMarketingTopic(lang: string, token: string, insiderUid: string, topic: string, source: string | null) {
    return fetchService.post('/insider/subscribe-to-topic/', lang, { token: token, id: insiderUid, topic: topic, source: source });
  }
  function subscribeToSingleMarketingTopicByEmail(lang: string, email: string, topic: string, insiderUid: string | undefined) {
    return fetchService.post('/insider/subscribe-to-topic-by-email/', lang, { email: email, topic: topic, id: insiderUid, source: route.path });
  }

  return {
    canCreateCampsiteMedia,
    changeMail,
    changePassword,
    checkUser,
    confirmDoubleOptin,
    getMarketingPreferences,
    getMedia,
    getMediaForCampsite,
    getProfile,
    getProfileLanguages,
    getReviews,
    login,
    logout,
    register,
    resendVerificationEmail,
    resetPassword,
    saveMarketingPreferences,
    setNewPassword,
    setInitialPassword,
    setProfile,
    setProfilePicture,
    subscribeToSingleMarketingTopic,
    subscribeToSingleMarketingTopicByEmail,
    toggleFavorite,
    update,
    updateInsider,
    verifyChangedEmail,
    verifyEmail,
  };
}
